import { useState } from 'react'
import { ChevronDown, RotateCcw } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ColorPickerField from '@/components/Indicators/ColorPickerField'
import SliderField from '@/components/Indicators/SliderField'
import ToggleField from '@/components/Indicators/ToggleField'
import TelegramNotificationsSection from '@/components/Indicators/TelegramNotificationsSection'
import { defaultConfig } from './index'

function ToggleRow({ label, value, onChange }) {
  return (
    <label className="flex items-center justify-between cursor-pointer py-0.5">
      <span className="text-xs text-[var(--txt-dim,#B9BDB4)]">{label}</span>
      <input
        type="checkbox"
        checked={value}
        onChange={e => onChange(e.target.checked)}
        className="w-3.5 h-3.5 rounded border-[var(--hair,#B08D57)] text-[#0B3D2E] focus:ring-[#B08D57] focus:ring-1"
      />
    </label>
  )
}

function Section({ title, children, sectionKey, config, onChange }) {
  const open = config?.panelSections?.[sectionKey] ?? false
  const toggle = () => onChange({ panelSections: { ...(config.panelSections || {}), [sectionKey]: !open } })
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">
          {children}
        </div>
      )}
    </div>
  )
}

const STYLE_KEYS = [
  'markerSize', 'haloOpacity', 'cloudOpacity', 'baseLineWidth', 'baseLineStyle',
  'memberLineWidth', 'memberLineStyle', 'labelFont', 'labelBgOpacity', 'plaqueStyle',
]
const LINE_STYLES = [
  { value: 'solid', label: 'Sólido' },
  { value: 'dotted', label: 'Puntos' },
  { value: 'dashed', label: 'Guiones' },
  { value: 'largeDashed', label: 'Largo' },
  { value: 'sparseDotted', label: 'Ralo' },
]

export default function SuperTrendQuantumPanel({ config, onChange, onClose, chartSymbol, chartTimeframe }) {
  const cfg = config || {}

  const update = (key, value) => {
    onChange({ ...cfg, [key]: value })
  }

  const updatePanelSection = (sectionKey, open) => {
    onChange({ panelSections: { ...(cfg.panelSections || {}), [sectionKey]: open } })
  }

  const resetStyle = () => {
    const patch = {}
    for (const k of STYLE_KEYS) patch[k] = defaultConfig[k]
    onChange({ ...cfg, ...patch })
  }

  return (
    <IndicatorConfigModal title="Configuración SuperTrend Quantum" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
        <Section title="Lógica" sectionKey="logic" config={cfg} onChange={onChange}>
          <SliderField
            label="Umbral de consenso"
            value={cfg.consensus_threshold ?? 0.60}
            min={0.1}
            max={1.0}
            step={0.01}
            unit=""
            onChange={(v) => update('consensus_threshold', v)}
          />
          <SliderField
            label="Miembro base"
            value={cfg.base_index ?? 3}
            min={1}
            max={5}
            step={1}
            unit=""
            onChange={(v) => update('base_index', v)}
          />
          <SliderField
            label="Nube de referencia"
            value={cfg.cloud_length ?? 8}
            min={1}
            max={50}
            step={1}
            unit=""
            onChange={(v) => update('cloud_length', v)}
          />
        </Section>

        <Section title="Colores" sectionKey="colors" config={cfg} onChange={onChange}>
          <ColorPickerField label="Alcista" value={cfg.colorBull ?? '#35A07A'} onChange={(v) => update('colorBull', v)} />
          <ColorPickerField label="Bajista" value={cfg.colorBear ?? '#B4574B'} onChange={(v) => update('colorBear', v)} />
          <ColorPickerField label="Neutro" value={cfg.colorNeutral ?? '#B08D57'} onChange={(v) => update('colorNeutral', v)} />
          <SliderField
            label="Grosor de línea cluster"
            value={cfg.lineWidth ?? 1.6}
            min={1}
            max={3}
            step={0.5}
            unit="px"
            onChange={(v) => update('lineWidth', v)}
          />
        </Section>

        <Section title="Visuales" sectionKey="visual" config={cfg} onChange={onChange}>
          <ToggleRow label="Mostrar dashboard" value={cfg.showDashboard ?? true} onChange={(v) => update('showDashboard', v)} />
          <ToggleRow label="Caja LONG/SHORT" value={cfg.showPositionBox ?? true} onChange={(v) => update('showPositionBox', v)} />
          <ToggleRow label="Mostrar nube (ribbon)" value={cfg.show_cloud ?? true} onChange={(v) => update('show_cloud', v)} />
          <ToggleRow label="Mostrar línea base" value={cfg.show_base_line ?? true} onChange={(v) => update('show_base_line', v)} />
          <ToggleRow label="Dots de giro base" value={cfg.show_base_flip_dots ?? true} onChange={(v) => update('show_base_flip_dots', v)} />
          <ToggleRow label="Mostrar señales" value={cfg.show_signals ?? true} onChange={(v) => update('show_signals', v)} />
          <ToggleRow label="Etiquetas de cluster" value={cfg.show_cluster_labels ?? true} onChange={(v) => update('show_cluster_labels', v)} />
          <ToggleRow label="Etiqueta de precio en eje" value={cfg.showPriceLabel ?? true} onChange={(v) => update('showPriceLabel', v)} />
          <ToggleRow label="Colorear velas" value={cfg.dynamic_bar_coloring ?? false} onChange={(v) => update('dynamic_bar_coloring', v)} />
          <ToggleRow label="Líneas de miembros (debug)" value={cfg.show_member_lines ?? false} onChange={(v) => update('show_member_lines', v)} />
        </Section>

        <Section title="Estilo avanzado" sectionKey="style" config={cfg} onChange={onChange}>
          <div className="space-y-2">
            <SliderField
              label="Opacidad de nube"
              value={cfg.cloudOpacity ?? 0.18}
              min={0}
              max={0.5}
              step={0.01}
              unit=""
              onChange={(v) => update('cloudOpacity', v)}
            />
            <SliderField
              label="Tamaño de marcadores"
              value={cfg.markerSize ?? 1}
              min={0}
              max={3}
              step={1}
              unit=""
              onChange={(v) => update('markerSize', v)}
            />
            <SliderField
              label="Opacidad de halo"
              value={cfg.haloOpacity ?? 0.25}
              min={0}
              max={1}
              step={0.01}
              unit=""
              onChange={(v) => update('haloOpacity', v)}
            />

            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-[10px] text-[var(--ink-soft,#4A5A52)]">Línea base</span>
                <SliderField
                  label="Grosor"
                  value={cfg.baseLineWidth ?? 1}
                  min={0.5}
                  max={3}
                  step={0.5}
                  unit="px"
                  onChange={(v) => update('baseLineWidth', v)}
                />
                <StyleSelect value={cfg.baseLineStyle ?? 'dotted'} onChange={(v) => update('baseLineStyle', v)} />
              </div>
              <div>
                <span className="text-[10px] text-[var(--ink-soft,#4A5A52)]">Líneas miembro</span>
                <SliderField
                  label="Grosor"
                  value={cfg.memberLineWidth ?? 1}
                  min={0.5}
                  max={3}
                  step={0.5}
                  unit="px"
                  onChange={(v) => update('memberLineWidth', v)}
                />
                <StyleSelect value={cfg.memberLineStyle ?? 'dotted'} onChange={(v) => update('memberLineStyle', v)} />
              </div>
            </div>

            <SliderField
              label="Opacidad fondo etiquetas"
              value={cfg.labelBgOpacity ?? 0.12}
              min={0}
              max={0.5}
              step={0.01}
              unit=""
              onChange={(v) => update('labelBgOpacity', v)}
            />
            <div className="space-y-1">
              <span className="text-xs text-[var(--ink-soft,#4A5A52)]">Fuente de etiquetas</span>
              <input
                type="text"
                value={cfg.labelFont ?? defaultConfig.labelFont}
                onChange={(e) => update('labelFont', e.target.value)}
                className="w-full px-2 py-1 text-[11px] rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-white/80 dark:bg-gray-900/50 text-[var(--ink,#15211B)]"
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-[var(--ink-soft,#4A5A52)]">Estilo de placa</span>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'plaque', label: 'Placa' },
                  { value: 'tooltip', label: 'Tooltip' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => update('plaqueStyle', opt.value)}
                    className={`px-2 py-1.5 text-[11px] font-medium rounded border transition-colors ${
                      cfg.plaqueStyle === opt.value
                        ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
                        : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={resetStyle}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
            >
              <RotateCcw size={12} />
              Restablecer estilo Quantum
            </button>
          </div>
        </Section>

        <Section title="Notificaciones" sectionKey="notifications" config={cfg} onChange={onChange}>
          <TelegramNotificationsSection
            indicatorId="supertrend_quantum"
            symbol={chartSymbol}
            timeframe={chartTimeframe}
          />
        </Section>
      </div>
    </IndicatorConfigModal>
  )
}

function StyleSelect({ value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-1 mt-1">
      {LINE_STYLES.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-1 py-1 text-[10px] font-medium rounded border transition-colors ${
            value === opt.value
              ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
              : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}
